import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Layers, Code2, Package, Server, ShieldCheck, Users,
  ArrowRight, Sparkles, AlertTriangle, Lightbulb,
  Cloud, LifeBuoy, Cpu
} from "lucide-react";
import "./NewProductDevelopment.css";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.1,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const specializations = [
  { icon: Layers, title: "Application Modernization", desc: "Define product roadmap & strategy to modernize legacy applications with zero downtime.", label: "Modernize" },
  { icon: Code2, title: "Technology Products", desc: "Tackle complex engineering challenges and large-scale development for technology clients.", label: "Engineer" },
  { icon: Package, title: "SaaS Products", desc: "Develop B2B and B2C custom solutions building core offerings and market differentiators.", label: "Launch" },
  { icon: Server, title: "Internal Products", desc: "Build the systems needed to support mission-critical operations and customer service delivery.", label: "Operate" },
];

const qaPoints = [
  { icon: ShieldCheck, title: "We Have Experience on Our Backs", ribbon: "Certified", desc: "Our entire workforce comprises industry-certified professionals who know tricks of the trade through experience and theory — giving us the sharp edge of knowledge and its correct application." },
  { icon: Users, title: "We Are on the Same Team as Our Clients", ribbon: "Partnered", desc: "The firm belief that benefits to you are benefits to us is the prime driving force that makes us put our 200% into testing effort. Partnering with us brings reliability, quality and a promise of better products in the market." },
];

const related = [
  { icon: LifeBuoy, title: "Product Support Services", to: "/services/product-support-services" },
  { icon: Cloud, title: "Cloud Solutions", to: "/services/cloud-solutions" },
  { icon: Cpu, title: "AI Enabled GCC Services", to: "/services/gcc-services" },
];

const NewProductDevelopment = () => {
  return (
    <div className="npd-page">
      {/* HERO — simple light */}
      <section className="npd-hero">
        <div className="npd-container">
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp}
            className="npd-hero-inner"
          >
            <span className="npd-eyebrow">
              <Sparkles size={14} /> Our Approach
            </span>
            <h1 className="npd-hero-title">
              New Product <span className="npd-hero-accent">Development</span>
            </h1>
            <p className="npd-hero-sub">
              In a dynamic business scenario, the most critical factor that holds the key to retain market leadership
              is your ability to launch and develop new products and services quickly and in a cost-efficient manner.
            </p>
            <div className="npd-hero-actions">
              <Link to="/contact" className="npd-cta">
                Connect with us <ArrowRight size={18} />
              </Link>
              <a href="#expertise" className="npd-cta npd-cta--ghost">
                Explore expertise
              </a>
            </div>

            {/* <div className="npd-hero-stats">
              <div>
                <div className="npd-hero-stat-num">50+</div>
                <div className="npd-hero-stat-label">Products Launched</div>
              </div>
              <div>
                <div className="npd-hero-stat-num">4x</div>
                <div className="npd-hero-stat-label">Faster Go-To-Market</div>
              </div>
              <div>
                <div className="npd-hero-stat-num">98%</div>
                <div className="npd-hero-stat-label">Client Retention</div>
              </div>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* CHALLENGE / SOLUTION */}
      <section className="npd-section container">
        <div className="npd-container">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="npd-section-head"
          >
            <span className="npd-tag">The Reality</span>
            <h2>From challenge to launch</h2>
            <p>We turn the chaos of modern product engineering into a structured, predictable journey.</p>
          </motion.div>

          <div className="npd-cs-grid">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="npd-cs-card npd-cs-card--challenge"
            >
              <span className="npd-corner npd-corner--tr" />
              <span className="npd-corner npd-corner--bl" />
              <div className="npd-cs-card-body">
                <div className="npd-cs-icon"><AlertTriangle size={28} /></div>
                <span className="npd-cs-badge">Challenge</span>
                <h3>The Market Reality</h3>
                <p>
                  Companies face challenges responding to scale, increased complexity, dynamics of globalization,
                  reduced costs, and reduced cycle times — making it harder to stay ahead of the curve.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}
              className="npd-cs-card npd-cs-card--solution"
            >
              <span className="npd-corner npd-corner--tr" />
              <span className="npd-corner npd-corner--bl" />
              <div className="npd-cs-card-body">
                <div className="npd-cs-icon"><Lightbulb size={28} /></div>
                <span className="npd-cs-badge npd-cs-badge--green">SpringUp Solution</span>
                <h3>Partnered for Success</h3>
                <p>
                  Our experienced team partners closely with clients to define, build, and evolve compelling,
                  high-performing product solutions to improve ROI and accelerate go-to-market — quickly and successfully.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SPECIALIZATIONS */}
      <section className="npd-section npd-section--alt " id="expertise">
        <div className="npd-container container">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="npd-section-head"
          >
            <span className="npd-tag">Our Expertise</span>
            <h2>Our team specializes in</h2>
            <p>Four pillars of product development tailored to modern engineering challenges.</p>
          </motion.div>

          <div className="npd-spec-grid">
            {specializations.map((s, i) => (
              <motion.div
                key={s.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="npd-spec-card"
              >
                <div className="npd-spec-top">
                  <div className="npd-spec-icon"><s.icon size={24} /></div>
                  <span className="npd-spec-num">0{i + 1}</span>
                </div>
                <span className="npd-spec-label">{s.label}</span>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QA */}
      <section className="npd-section">
        <div className="npd-container container">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="npd-section-head"
          >
            <span className="npd-tag npd-tag--blue">Independent Quality Assurance</span>
            <h2>Why partner with us</h2>
            <p>Trust built on certified expertise and shared incentives.</p>
          </motion.div>

          <div className="npd-qa-grid">
            {qaPoints.map((q, i) => (
              <motion.div
                key={q.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="npd-qa-card"
              >
                <span className="npd-qa-circle" />
                <div className="npd-qa-ribbon">{q.ribbon}</div>
                <div className="npd-qa-head">
                  <div className="npd-qa-orb"><q.icon size={28} /></div>
                  <h4>{q.title}</h4>
                </div>
                <div className="npd-qa-body">
                  <p>{q.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="npd-section npd-section--alt">
        <div className="npd-container">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="npd-related-title"
          >
            Related Services
          </motion.h2>
          <div className="npd-related-grid">
            {related.map((r, i) => (
              <motion.div
                key={r.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="npd-related-card"
              >
                <div className="npd-related-icon"><r.icon size={22} /></div>
                <h4>{r.title}</h4>
                <Link to={r.to} className="npd-related-link">
                  Learn more <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewProductDevelopment;
